import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { colaboradoresAPI } from '@/lib/api-client'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'

const CONTRACT_TYPE_LABELS: Record<string, string> = {
  CLT: 'CLT',
  PJ: 'Pessoa Jurídica (PJ)',
  ESTAGIO: 'Estágio',
  TEMPORARIO: 'Temporário',
  AUTONOMO: 'Autônomo',
}

const formSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  document: z.string().min(1, 'CPF é obrigatório'),
  cargo: z.string().min(1, 'Cargo é obrigatório'),
  department: z.string().optional(),
  contractType: z.enum(['CLT', 'PJ', 'ESTAGIO', 'TEMPORARIO', 'AUTONOMO']),
  admissionDate: z.string().optional(),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
  phone: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

interface ColaboradorFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  colaborador?: any
}

export function ColaboradorFormDialog({
  open,
  onOpenChange,
  colaborador,
}: ColaboradorFormDialogProps) {
  const queryClient = useQueryClient()
  const isEdit = !!colaborador

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: colaborador
      ? {
          name: colaborador.name,
          document: colaborador.document,
          cargo: colaborador.cargo,
          department: colaborador.department || '',
          contractType: colaborador.contractType || 'CLT',
          admissionDate: colaborador.admissionDate
            ? new Date(colaborador.admissionDate).toISOString().split('T')[0]
            : '',
          email: colaborador.email || '',
          phone: colaborador.phone || '',
        }
      : {
          name: '',
          document: '',
          cargo: '',
          department: '',
          contractType: 'CLT',
          admissionDate: '',
          email: '',
          phone: '',
        },
  })

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: any = {
        name: values.name,
        document: values.document.replace(/\D/g, ''),
        cargo: values.cargo,
        department: values.department || undefined,
        contractType: values.contractType,
        admissionDate: values.admissionDate
          ? new Date(values.admissionDate).toISOString()
          : undefined,
        email: values.email || undefined,
        phone: values.phone || undefined,
      }

      if (isEdit) {
        return colaboradoresAPI.update(colaborador.id, payload)
      }
      return colaboradoresAPI.create(payload)
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Colaborador atualizado!' : 'Colaborador cadastrado!')
      queryClient.invalidateQueries({ queryKey: ['colaboradores'] })
      if (isEdit) {
        queryClient.invalidateQueries({ queryKey: ['colaborador', colaborador.id] })
      }
      onOpenChange(false)
      form.reset()
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const onSubmit = (values: FormValues) => {
    mutation.mutate(values)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar Colaborador' : 'Novo Colaborador'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Atualize as informações do colaborador.'
              : 'Preencha as informações para cadastrar um novo colaborador.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-neutral-700">Informações Pessoais</h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  {...form.register('name')}
                  placeholder="Nome completo"
                />
                {form.formState.errors.name && (
                  <p className="text-sm text-destructive mt-1">{form.formState.errors.name.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="document">CPF *</Label>
                <Input
                  id="document"
                  {...form.register('document')}
                  placeholder="000.000.000-00"
                />
                {form.formState.errors.document && (
                  <p className="text-sm text-destructive mt-1">{form.formState.errors.document.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  {...form.register('email')}
                  placeholder="email@exemplo.com"
                />
                {form.formState.errors.email && (
                  <p className="text-sm text-destructive mt-1">{form.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  id="phone"
                  {...form.register('phone')}
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-medium text-neutral-700">Informações Profissionais</h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="cargo">Cargo *</Label>
                <Input
                  id="cargo"
                  {...form.register('cargo')}
                  placeholder="Ex: Pedreiro, Engenheiro, Técnico..."
                />
                {form.formState.errors.cargo && (
                  <p className="text-sm text-destructive mt-1">{form.formState.errors.cargo.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="department">Departamento</Label>
                <Input
                  id="department"
                  {...form.register('department')}
                  placeholder="Ex: Obras, Administrativo..."
                />
              </div>

              <div>
                <Label>Tipo de Contrato *</Label>
                <Select
                  value={form.watch('contractType')}
                  onValueChange={(value) =>
                    form.setValue('contractType', value as any)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CONTRACT_TYPE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="admissionDate">Data de Admissão</Label>
                <Input
                  id="admissionDate"
                  type="date"
                  {...form.register('admissionDate')}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Salvar' : 'Cadastrar Colaborador'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
